import Close16 from '@carbon/icons-react/es/close/16';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Account } from 'api/account';
import { CostRateManagerBaseProps, CostRateSourceType } from 'api/cost-rates';
import {
  CustomLayoutDrawer,
  CustomLayoutDrawerContent,
} from 'components/atoms/drawer';
import { Form } from 'components/atoms/form';
import { Loading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { INVALID_ROLE_ID } from 'components/organisms/resource-management/utils/common';
import { PermissionsContext } from 'contexts/permissions';
import { GranularPermissions, useHasAccess } from 'hooks/use-has-access';
import { useTranslation } from 'hooks/use-translation';
import {
  useCostRateDeleteMutation,
  useCostRateForResourceMutation,
  useCostRateForResourceQuery,
} from 'queries/cost-rates';
import { defaultFormat } from 'utils/date-field/formatter';
import { negativeUid } from 'utils/uid';
import { CostRateDrawerExtras } from '..';
import { CostRateUpdateConfirmationModal } from '../modal';
import { CostRateDrawerFooter } from './footer';
import { CostRateForm } from './form';
import { CostRateDrawerHeader } from './header';

const LoaderWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCustomDrawerContent = styled(CustomLayoutDrawerContent)`
  ${CustomLayoutDrawerContent.Title} {
    padding-left: var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Content} {
    padding-top: var(--spacing-06);
    padding-left: var(--spacing-06);
    padding-right: var(--spacing-06);
  }

  ${CustomLayoutDrawerContent.Footer} {
    padding: var(--spacing-06);
  }
`;

export interface CostRateFormProps {
  hourlyCostRate: number;
  clearCostRate: boolean;
  shouldBackdate: boolean;
  backdateFrom?: moment.Moment;
}

interface Props extends CostRateManagerBaseProps, CostRateDrawerExtras {
  account: Account;
  isVisible: boolean;
}

export const CostRateDrawer: FC<Props> = ({
  account,
  sourceId,
  sourceType,
  isVisible,
  resourceName,
  userLookup,
  onClose,
}) => {
  const { hasAccess: canWrite } = useHasAccess({
    permissions: ['WRITE'],
    resource: 'costRate',
  });

  const costRatePermissions: GranularPermissions = useMemo(
    () => ({
      canWrite,
    }),
    [canWrite],
  );

  const successToastTitle = useTranslation(
    'settings.cost_rates.update.toast.success.title',
  );
  const successToastMessage = useTranslation(
    'settings.cost_rates.update.toast.success.message',
  );
  const errorToastTitle = useTranslation(
    'settings.cost_rates.update.toast.error.title',
  );
  const errorToastMessage = useTranslation(
    'settings.cost_rates.update.toast.error.message',
  );

  const [form] = Form.useForm<CostRateFormProps>();

  const { data: costRate, isLoading: isCostRateLoading } =
    useCostRateForResourceQuery({
      sourceId,
      sourceType,
    });

  const { data: fallbackCostRate, isLoading: isFallbackCostRateLoading } =
    useCostRateForResourceQuery(
      {
        sourceId: userLookup[sourceId]?.role?.roleId ?? INVALID_ROLE_ID,
        sourceType: CostRateSourceType.Role,
      },
      {
        enabled:
          sourceType === CostRateSourceType.User &&
          userLookup[sourceId].role != null,
      },
    );
  const { mutateAsync: updateCostRate, isLoading: isMutating } =
    useCostRateForResourceMutation();
  const { mutateAsync: deleteCostRate, isLoading: isDeleting } =
    useCostRateDeleteMutation();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);

  const isDataLoading = useMemo(
    () =>
      sourceType === CostRateSourceType.User &&
      userLookup[sourceId].role != null
        ? isFallbackCostRateLoading || isCostRateLoading
        : isCostRateLoading,
    [
      isCostRateLoading,
      isFallbackCostRateLoading,
      sourceId,
      sourceType,
      userLookup,
    ],
  );

  const onCloseDrawer = useCallback(() => {
    onClose();
    form.resetFields();
  }, [form, onClose]);

  const onCloseModal = useCallback(() => {
    setShowConfirmationModal(false);
  }, []);

  const onSave = useCallback(async () => {
    const { clearCostRate, hourlyCostRate, backdateFrom } =
      form.getFieldsValue();

    const backdate = defaultFormat(backdateFrom ?? moment().startOf('day'));

    try {
      if (clearCostRate) {
        await deleteCostRate({
          sourceId,
          sourceType,
          backdate,
        });
      } else {
        await updateCostRate({
          costRateId: costRate?.costRateId ?? negativeUid(),
          sourceId,
          sourceType,
          hourlyCostRate,
          currency: account.currency,
          backdate,
        });
      }

      onCloseModal();
      onCloseDrawer();
      toastService({
        title: successToastTitle,
        message: successToastMessage,
      }).success();
    } catch (e) {
      toastService({
        title: errorToastTitle,
        message: errorToastMessage,
      }).error();
    }
  }, [
    account.currency,
    costRate?.costRateId,
    deleteCostRate,
    errorToastMessage,
    errorToastTitle,
    form,
    onCloseDrawer,
    onCloseModal,
    sourceId,
    sourceType,
    successToastMessage,
    successToastTitle,
    updateCostRate,
  ]);

  const checkCanSubmit = useCallback(() => {
    const { hourlyCostRate, clearCostRate } = form.getFieldsValue();

    setIsSubmitDisabled(
      clearCostRate
        ? (costRate?.hourlyCostRate == null) === (clearCostRate ?? false)
        : // eslint-disable-next-line eqeqeq
          costRate?.hourlyCostRate == hourlyCostRate || hourlyCostRate == null,
    );
    // setIsSubmitDisabled(false);
  }, [costRate, form]);

  useEffect(() => {
    checkCanSubmit();
  }, [checkCanSubmit]);

  return (
    <PermissionsContext.Provider value={costRatePermissions}>
      <CustomLayoutDrawer
        placement="right"
        visible={isVisible}
        width={640}
        mask
        onClose={onCloseDrawer}
        closeIcon={<Close16 />}
        destroyOnClose
      >
        {isDataLoading ? (
          <LoaderWrapper>
            <Loading size="large" />
          </LoaderWrapper>
        ) : (
          <StyledCustomDrawerContent>
            <CustomLayoutDrawerContent.Title>
              <CostRateDrawerHeader resourceName={resourceName} />
            </CustomLayoutDrawerContent.Title>
            <CustomLayoutDrawerContent.Content>
              <CostRateForm
                form={form}
                account={account}
                sourceType={sourceType}
                costRate={costRate}
                fallbackCostRate={fallbackCostRate}
                checkCanSubmit={checkCanSubmit}
              />
            </CustomLayoutDrawerContent.Content>
            <CustomLayoutDrawerContent.Footer>
              <CostRateDrawerFooter
                costRate={costRate}
                userLookup={userLookup}
                isLoading={isMutating || isDeleting}
                isSubmitDisabled={isSubmitDisabled}
                onSave={() => {
                  setShowConfirmationModal(true);
                }}
                onClose={onCloseDrawer}
              />
            </CustomLayoutDrawerContent.Footer>
          </StyledCustomDrawerContent>
        )}
      </CustomLayoutDrawer>
      <CostRateUpdateConfirmationModal
        form={form}
        isVisible={showConfirmationModal}
        isLoading={isMutating}
        onClose={onCloseModal}
        onSave={onSave}
      />
    </PermissionsContext.Provider>
  );
};
